import React, { ProfilerOnRenderCallback, ProfilerProps } from 'react';

/**
 * Performance profiling utility for React components
 * 
 * Usage:
 *   <PerformanceProfiler id="ComponentName">
 *     <YourComponent />
 *   </PerformanceProfiler>
 */

interface PerformanceProfilerProps {
  id: string;
  children: React.ReactNode;
  enabled?: boolean;
  logToConsole?: boolean;
  logSlowRenders?: boolean;
  slowRenderThreshold?: number; // ms
}

// Enable profiling in development by default
// In Vite, import.meta.env.DEV is true in development, false in production
// Must use direct access (not optional chaining) for SSR compatibility
const isDev = import.meta.env.DEV === true;

// Global render tracking for cascade detection
const renderHistory: Array<{
  id: string;
  phase: 'mount' | 'update';
  timestamp: number;
  actualDuration: number;
  startTime: number;
  commitTime: number;
}> = [];

// Keep last 50 renders for analysis
const MAX_HISTORY = 50;

// Track render cascades (multiple components rendering within a short time)
function analyzeRenderCascade() {
  if (renderHistory.length < 2) return null;
  
  const recent = renderHistory.slice(-10); // Last 10 renders
  const cascade: string[] = [];
  let cascadeStart = recent[0].timestamp;
  const CASCADE_THRESHOLD = 50; // ms - renders within 50ms are considered a cascade
  
  for (let i = 1; i < recent.length; i++) {
    const timeSincePrev = recent[i].timestamp - recent[i - 1].timestamp;
    if (timeSincePrev < CASCADE_THRESHOLD) {
      if (cascade.length === 0) {
        cascade.push(recent[i - 1].id);
        cascadeStart = recent[i - 1].timestamp;
      }
      cascade.push(recent[i].id);
    } else if (cascade.length > 0) {
      break;
    }
  }
  
  return cascade.length > 2 ? {
    components: cascade,
    duration: recent[cascade.length - 1].timestamp - cascadeStart,
    count: cascade.length,
  } : null;
}

// Track rapid re-renders of the same component
function analyzeRapidRerenders(componentId: string, timestamp: number) {
  const recent = renderHistory.filter(r => r.id === componentId).slice(-5);
  if (recent.length < 2) return null;
  
  const lastRender = recent[recent.length - 2];
  const timeSinceLastRender = timestamp - lastRender.timestamp;
  const RAPID_THRESHOLD = 100; // ms
  
  if (timeSinceLastRender < RAPID_THRESHOLD) {
    return {
      component: componentId,
      timeSinceLastRender,
      renderCount: recent.length,
    };
  }
  
  return null;
}

export function PerformanceProfiler({
  id,
  children,
  enabled = isDev, // Default to enabled in development
  logToConsole = true,
  logSlowRenders = true,
  slowRenderThreshold = 16, // ~1 frame at 60fps
}: PerformanceProfilerProps) {
  if (!enabled) {
    return <>{children}</>;
  }

  const onRenderCallback: ProfilerOnRenderCallback = (
    id: string,
    phase: 'mount' | 'update',
    actualDuration: number,
    baseDuration: number,
    startTime: number,
    commitTime: number,
  ) => {
    const timestamp = Date.now();
    const isSlow = actualDuration > slowRenderThreshold;
    
    // Add to render history
    renderHistory.push({
      id,
      phase,
      timestamp,
      actualDuration,
      startTime,
      commitTime,
    });
    
    // Keep history size manageable
    if (renderHistory.length > MAX_HISTORY) {
      renderHistory.shift();
    }
    
    // Analyze patterns
    const cascade = analyzeRenderCascade();
    const rapidRerender = analyzeRapidRerenders(id, timestamp);
    
    if (logToConsole) {
      const style = isSlow ? 'color: red; font-weight: bold;' : 'color: blue;';
      
      // Build detailed log object
      const logData: any = {
        phase,
        actualDuration: `${actualDuration.toFixed(2)}ms`,
        baseDuration: `${baseDuration.toFixed(2)}ms`,
        startTime: `${startTime.toFixed(2)}ms`,
        commitTime: `${commitTime.toFixed(2)}ms`,
      };
      
      // Add cascade info if detected
      if (cascade && cascade.count > 2) {
        logData.CASCADE = {
          components: cascade.components.join(' → '),
          duration: `${cascade.duration.toFixed(2)}ms`,
          count: cascade.count,
        };
      }
      
      // Add rapid rerender info if detected
      if (rapidRerender) {
        logData.RAPID_RERENDER = {
          timeSinceLastRender: `${rapidRerender.timeSinceLastRender.toFixed(2)}ms`,
          renderCount: rapidRerender.renderCount,
        };
      }
      
      // Calculate time since last render of any component
      if (renderHistory.length > 1) {
        const prevRender = renderHistory[renderHistory.length - 2];
        const timeSinceLastRender = timestamp - prevRender.timestamp;
        logData.timeSinceLastRender = `${timeSinceLastRender.toFixed(2)}ms`;
        logData.lastComponent = prevRender.id;
      }
      
      console.log(
        `%c[Perf] ${id} - ${phase}`,
        style,
        logData
      );
      
      // Enhanced slow render warning
      if (isSlow && logSlowRenders) {
        const phaseLabel = phase === 'mount' ? 'MOUNT (initial render)' : 'UPDATE (re-render)';
        let warningMsg = `%c[Perf] ⚠️ SLOW RENDER: ${id} [${phaseLabel}] took ${actualDuration.toFixed(2)}ms (threshold: ${slowRenderThreshold}ms)`;
        
        if (cascade && cascade.count > 2) {
          warningMsg += `\n  🔄 CASCADE: ${cascade.components.join(' → ')} (${cascade.count} components in ${cascade.duration.toFixed(2)}ms)`;
        }
        
        if (rapidRerender) {
          warningMsg += `\n  ⚡ RAPID: Re-rendered ${rapidRerender.renderCount}x (last ${rapidRerender.timeSinceLastRender.toFixed(2)}ms ago)`;
        }
        
        console.warn(
          warningMsg,
          'color: red; font-weight: bold; font-size: 14px;'
        );
        
        // Log stack trace for slow renders in development
        if (isDev && typeof Error !== 'undefined') {
          console.groupCollapsed(`%cStack trace for ${id}`, 'color: orange;');
          console.trace();
          console.groupEnd();
        }
      }
    }

    // Use Performance API for detailed profiling
    if (typeof window !== 'undefined' && window.performance && window.performance.mark) {
      const markName = `perf-${id}-${phase}-${commitTime}`;
      window.performance.mark(markName);
      
      // Measure time since last render
      const lastMarkName = `perf-${id}-last-${phase}`;
      const lastMark = window.performance.getEntriesByName(lastMarkName, 'mark')[0];
      if (lastMark) {
        window.performance.measure(
          `${id}-${phase}-interval`,
          lastMarkName,
          markName
        );
      }
      window.performance.mark(lastMarkName);
    }
  };

  return (
    <React.Profiler id={id} onRender={onRenderCallback}>
      {children}
    </React.Profiler>
  );
}

/**
 * Hook to measure execution time of a function
 */
export function usePerformanceTimer(label: string, enabled: boolean = true) {
  const startTimeRef = React.useRef<number | null>(null);

  const start = React.useCallback(() => {
    if (!enabled) return;
    if (typeof window !== 'undefined' && window.performance) {
      startTimeRef.current = performance.now();
      performance.mark(`${label}-start`);
    } else {
      startTimeRef.current = Date.now();
    }
    console.time(label);
  }, [label, enabled]);

  const end = React.useCallback(() => {
    if (!enabled || startTimeRef.current === null) return;
    
    if (typeof window !== 'undefined' && window.performance) {
      const endTime = performance.now();
      const duration = endTime - startTimeRef.current;
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      console.timeEnd(label);
      console.log(`[Perf Timer] ${label}: ${duration.toFixed(2)}ms`);
    } else {
      const duration = Date.now() - startTimeRef.current;
      console.timeEnd(label);
      console.log(`[Perf Timer] ${label}: ${duration}ms`);
    }
    startTimeRef.current = null;
  }, [label, enabled]);

  return { start, end };
}

// Helper to detect if values are deeply equal (for objects/arrays)
function areValuesDeeplyEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  
  if (typeof a === 'object') {
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    
    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      return a.every((val, idx) => areValuesDeeplyEqual(val, b[idx]));
    }
    
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => areValuesDeeplyEqual(a[key], b[key]));
  }
  
  return false;
}

// Helper to get a simplified representation of a value for logging
function getValueSummary(value: any): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value.length > 50 ? `"${value.substring(0, 50)}..." (${value.length} chars)` : JSON.stringify(value);
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return `Array(${value.length})`;
    }
    const keys = Object.keys(value);
    return `{${keys.length} keys: ${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '...' : ''}}`;
  }
  return String(value);
}

/**
 * Hook to log render information with detailed change tracking
 */
export function useRenderLogger(componentName: string, dependencies: Record<string, any> = {}) {
  const renderCountRef = React.useRef(0);
  const prevDepsRef = React.useRef<Record<string, any>>({});
  const renderTimesRef = React.useRef<number[]>([]);
  const enabled = isDev; // Always enabled in development

  React.useEffect(() => {
    if (!enabled) return;
    
    const renderTime = Date.now();
    renderCountRef.current += 1;
    const renderCount = renderCountRef.current;
    
    // Find what changed (both shallow and deep equality checks)
    const changedDeps: string[] = [];
    const changes: Record<string, { from: any; to: any; shallow: boolean; deep: boolean }> = {};
    
    Object.keys(dependencies).forEach(key => {
      const prevValue = prevDepsRef.current[key];
      const currValue = dependencies[key];
      
      // Check shallow equality first
      const shallowEqual = prevValue === currValue;
      
      // If not shallow equal, check deep equality
      const valuesAreDeeplyEqual = !shallowEqual && typeof prevValue === 'object' && typeof currValue === 'object'
        ? areValuesDeeplyEqual(prevValue, currValue)
        : shallowEqual;
      
      if (!shallowEqual) {
        changedDeps.push(key);
        // deep: true means values are deeply different, false means only reference changed (same content)
        changes[key] = {
          from: prevValue,
          to: currValue,
          shallow: false,
          deep: valuesAreDeeplyEqual ? false : true,
        };
      }
    });
    
    // Calculate time since last render
    let timeSinceLastRender: number | null = null;
    if (renderTimesRef.current.length > 0) {
      const lastRenderTime = renderTimesRef.current[renderTimesRef.current.length - 1];
      timeSinceLastRender = renderTime - lastRenderTime;
    }
    renderTimesRef.current.push(renderTime);
    if (renderTimesRef.current.length > 10) {
      renderTimesRef.current.shift(); // Keep last 10 render times
    }
    
    const isInitialRender = renderCount === 1;
    const logLevel = changedDeps.length > 0 ? 'log' : isInitialRender ? 'log' : 'debug';
    const style = changedDeps.length > 0 ? 'color: orange; font-weight: bold;' : 'color: green; font-weight: bold;';
    
    // Build detailed change information
    const detailedChanges: Record<string, any> = {};
    if (changedDeps.length > 0) {
      changedDeps.forEach(key => {
        const change = changes[key];
        detailedChanges[key] = {
          from: getValueSummary(change.from),
          to: getValueSummary(change.to),
          referenceChanged: change.deep === false, // If not deep changed, only reference changed (new object/array with same content)
        };
      });
    }
    
    if (logLevel === 'debug' && typeof console.debug === 'function') {
      console.debug(
        `%c[Render] ${componentName} #${renderCount} (no changes)${timeSinceLastRender !== null ? ` - ${timeSinceLastRender.toFixed(2)}ms since last` : ''}`,
        'color: gray;',
        {
          timeSinceLastRender: timeSinceLastRender !== null ? `${timeSinceLastRender.toFixed(2)}ms` : 'N/A',
          dependencies: Object.keys(dependencies).reduce((acc, key) => {
            acc[key] = getValueSummary(dependencies[key]);
            return acc;
          }, {} as Record<string, any>),
        }
      );
    } else {
      const logObj: any = {
        renderCount,
        changed: changedDeps.length > 0 ? changedDeps : isInitialRender ? ['(initial render)'] : ['(no changes)'],
      };
      
      if (changedDeps.length > 0) {
        logObj.changes = detailedChanges;
        logObj.hasReferenceChanges = Object.values(changes).some(c => !c.deep);
      }
      
      if (timeSinceLastRender !== null) {
        logObj.timeSinceLastRender = `${timeSinceLastRender.toFixed(2)}ms`;
        
        // Warn about rapid re-renders
        if (timeSinceLastRender < 50 && renderCount > 1) {
          logObj.RAPID_RERENDER = true;
        }
      }
      
      console.log(
        `%c[Render] ${componentName} #${renderCount}${changedDeps.length > 0 ? ` - CHANGED: ${changedDeps.join(', ')}` : isInitialRender ? ' (initial)' : ''}${timeSinceLastRender !== null && timeSinceLastRender < 50 ? ` ⚡ RAPID (${timeSinceLastRender.toFixed(2)}ms)` : ''}`,
        style,
        logObj
      );
      
      // Warn about reference-only changes (new object/array with same content)
      if (changedDeps.length > 0 && Object.values(changes).every(c => c.deep === false)) {
        console.warn(
          `%c[Render] ${componentName}: Props changed but content is the same (reference changed). Consider memoizing.`,
          'color: yellow; font-weight: bold;',
          detailedChanges
        );
      }
    }
    
    prevDepsRef.current = { ...dependencies };
  });
}

/**
 * Wrapper to measure async operations
 */
export async function measureAsync<T>(
  label: string,
  fn: () => Promise<T>,
  enabled: boolean = isDev
): Promise<T> {
  if (!enabled) {
    return fn();
  }

  const startMark = `${label}-start`;
  const endMark = `${label}-end`;
  
  if (typeof window !== 'undefined' && window.performance) {
    performance.mark(startMark);
  }
  console.time(label);
  
  try {
    const result = await fn();
    
    if (typeof window !== 'undefined' && window.performance) {
      performance.mark(endMark);
      performance.measure(label, startMark, endMark);
    }
    console.timeEnd(label);
    
    return result;
  } catch (error) {
    console.timeEnd(label);
    console.error(`[Perf] Error in ${label}:`, error);
    throw error;
  }
}

/**
 * Wrapper to measure synchronous operations
 */
export function measureSync<T>(
  label: string,
  fn: () => T,
  enabled: boolean = isDev
): T {
  if (!enabled) {
    return fn();
  }

  const startMark = `${label}-start`;
  const endMark = `${label}-end`;
  
  if (typeof window !== 'undefined' && window.performance) {
    performance.mark(startMark);
  }
  console.time(label);
  
  try {
    const result = fn();
    
    if (typeof window !== 'undefined' && window.performance) {
      performance.mark(endMark);
      performance.measure(label, startMark, endMark);
    }
    console.timeEnd(label);
    
    return result;
  } catch (error) {
    console.timeEnd(label);
    console.error(`[Perf] Error in ${label}:`, error);
    throw error;
  }
}
